import { Link, createFileRoute } from '@tanstack/react-router'
import { FilterX } from 'lucide-react'
import { Boton } from '@/ui/Boton'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { FechaHora } from '@/ui/Numero'
import { useAuditoriaConsola } from '@/dominio/consola/useConsola'
import { EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { DetalleJson, ErrorDeCarga } from './-comun'

/**
 * La auditoría de la consola (`ecf.auditoria_consola`): qué hizo cada
 * operador, cuándo, sobre qué cliente y desde dónde.
 *
 * Solo se agrega; no hay nada que editar ni borrar aquí. Se pagina hacia
 * atrás en el tiempo, de cien en cien. El filtro por cliente va en la URL: se
 * llega a él pulsando el nombre de un cliente en la propia tabla.
 */
export const Route = createFileRoute('/consola/auditoria')({
  validateSearch: (busqueda: Record<string, unknown>): { tenant?: string; nombre?: string } => ({
    tenant: typeof busqueda.tenant === 'string' && busqueda.tenant !== '' ? busqueda.tenant : undefined,
    nombre: typeof busqueda.nombre === 'string' && busqueda.nombre !== '' ? busqueda.nombre : undefined,
  }),
  component: AuditoriaConsola,
})

function AuditoriaConsola() {
  const { tenant, nombre } = Route.useSearch()
  const consulta = useAuditoriaConsola(tenant)
  const eventos = consulta.data?.pages.flat() ?? []
  const th = 'h-[var(--alto-fila)] px-2 text-left text-etiqueta font-semibold text-gris-600'

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Auditoría de la consola"
        descripcion="Todo lo que hacen los operadores. Lo que se hace dentro de un cliente se anota también en la auditoría que ve ese cliente."
      />

      <Tarjeta
        titulo={tenant ? `Solo ${nombre ?? 'un cliente'}` : 'Todos los clientes'}
        relleno={false}
        acciones={
          tenant ? (
            <Link to="/consola/auditoria" search={{}}>
              <Boton tamano="menor" variante="discreto" icono={<FilterX />}>
                Quitar el filtro
              </Boton>
            </Link>
          ) : null
        }
      >
        {consulta.isError ? (
          <ErrorDeCarga error={consulta.error} queCargaba="la auditoría" onReintentar={() => void consulta.refetch()} />
        ) : !consulta.isLoading && eventos.length === 0 ? (
          <EstadoVacio
            variante={tenant ? 'sin-resultados' : 'resuelto'}
            mensaje={tenant ? `Ningún operador ha actuado sobre ${nombre ?? 'este cliente'}.` : 'Sin eventos todavía.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] table-fixed border-collapse text-cuerpo" data-densidad="normal">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300">
                  <th scope="col" className={`${th} w-[150px] text-right`}>
                    Cuándo
                  </th>
                  <th scope="col" className={`${th} w-[220px]`}>
                    Operador
                  </th>
                  <th scope="col" className={`${th} w-[200px]`}>
                    Acción
                  </th>
                  <th scope="col" className={`${th} w-[240px]`}>
                    Cliente
                  </th>
                  <th scope="col" className={th}>
                    Detalle
                  </th>
                  <th scope="col" className={`${th} w-[130px] text-right`}>
                    IP
                  </th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading ? (
                  <FilasEsqueleto filas={8} columnas={6} />
                ) : (
                  eventos.map((e) => (
                    <tr key={e.id} className="h-[var(--alto-fila)] border-b border-gris-200 align-top last:border-b-0">
                      <td className="numero px-2 py-2 text-gris-700">
                        <FechaHora valor={e.ocurridoEl} />
                      </td>
                      <td className="truncate px-2 py-2 text-gris-900" title={e.operador}>
                        {e.operador}
                      </td>
                      <td className="truncate px-2 py-2" title={e.accion}>
                        <span className="mono text-gris-900">{e.accion}</span>
                      </td>
                      <td className="truncate px-2 py-2">
                        {e.tenantId ? (
                          <span className="flex min-w-0 items-baseline gap-2">
                            <Link
                              to="/consola/clientes/$id"
                              params={{ id: e.tenantId }}
                              className="truncate text-gris-900 hover:text-marca hover:underline"
                              title={e.razonSocial ?? e.tenantId}
                            >
                              {e.razonSocial ?? e.tenantId}
                            </Link>
                            {tenant ? null : (
                              <Link
                                to="/consola/auditoria"
                                search={{ tenant: e.tenantId, nombre: e.razonSocial ?? undefined }}
                                className="shrink-0 text-etiqueta text-gris-600 hover:text-marca"
                              >
                                filtrar
                              </Link>
                            )}
                          </span>
                        ) : (
                          <span className="text-gris-400">plataforma</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <DetalleJson valor={e.detalle} />
                      </td>
                      <td className="mono px-2 py-2 text-right text-etiqueta text-gris-600">{e.ip ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {consulta.hasNextPage ? (
          <div className="flex justify-center border-t border-gris-300 p-3">
            <Boton
              variante="secundario"
              tamano="menor"
              onClick={() => void consulta.fetchNextPage()}
              disabled={consulta.isFetchingNextPage}
            >
              {consulta.isFetchingNextPage ? 'Cargando…' : 'Cargar anteriores'}
            </Boton>
          </div>
        ) : null}
      </Tarjeta>
    </div>
  )
}
